import { useEffect, useRef, useState } from "react";
import { StyleSheet, View, useWindowDimensions } from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Heart, HelpCircle, Users } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import type { OnboardingStackParamList, PartnerChoice } from "../types";
import { useOnboarding } from "../OnboardingContext";
import OnboardingLayout, {
  ScreenSubtitle,
  ScreenTitle,
} from "../components/OnboardingLayout";
import GlassChoiceButton from "../components/GlassChoiceButton";
import { getOnboardingViewport } from "../onboardingViewport";
import { AUTO_ADVANCE_DELAY } from "../theme";
import { routeAfterFamilyBuildingPath } from "../onboardingFlow";

type Props = NativeStackScreenProps<
  OnboardingStackParamList,
  "FamilyBuildingPath"
>;

type PartnerContextChoice = Exclude<PartnerChoice, "using-donor" | "unsure">;
type PathChoice = PartnerContextChoice | "unsure";

const OPTIONS: {
  value: PathChoice;
  labelKey: string;
  sublabelKey: string;
  Icon: typeof Heart;
}[] = [
  {
    value: "with-partner",
    labelKey: "onboarding.family_building_path.with_partner_label",
    sublabelKey: "onboarding.family_building_path.with_partner_sublabel",
    Icon: Users,
  },
  {
    value: "solo",
    labelKey: "onboarding.family_building_path.on_my_own_label",
    sublabelKey: "onboarding.family_building_path.on_my_own_sublabel",
    Icon: Heart,
  },
  {
    value: "unsure",
    labelKey: "onboarding.family_building_path.unsure_label",
    sublabelKey: "onboarding.family_building_path.unsure_sublabel",
    Icon: HelpCircle,
  },
];

export default function FamilyBuildingPathScreen({ navigation }: Props) {
  const { width, height } = useWindowDimensions();
  const { condensed } = getOnboardingViewport(width, height);
  const compactChoices = condensed || width < 430;
  const t = useTranslations();
  const { data, updateField } = useOnboarding();
  const initialPartnerChoice: PathChoice | null =
    data.partnerChoice === "with-partner" ||
    data.partnerChoice === "solo" ||
    data.partnerChoice === "unsure"
      ? data.partnerChoice
      : null;
  const [selected, setSelected] = useState<PathChoice | null>(
    initialPartnerChoice,
  );
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const advancing = useRef(false);

  useEffect(
    () => () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    },
    [],
  );

  const handleSelect = (choice: PathChoice) => {
    if (advancing.current) return;
    advancing.current = true;
    setSelected(choice);

    updateField("partnerChoice", choice);
    updateField("treatmentIntent", null);
    updateField("fertilityStage", null);
    updateField("gender", null);
    updateField("journeyRole", null);

    advanceTimer.current = setTimeout(() => {
      advancing.current = false;
      advanceTimer.current = null;
      const nextRoute = routeAfterFamilyBuildingPath();
      navigation.navigate(nextRoute.name);
    }, AUTO_ADVANCE_DELAY);
  };

  return (
    <OnboardingLayout>
      <ScreenTitle>{t("onboarding.family_building_path.title")}</ScreenTitle>
      <ScreenSubtitle>
        {t("onboarding.family_building_path.subtitle")}
      </ScreenSubtitle>
      <View style={[styles.options, compactChoices && styles.optionsCompact]}>
        {OPTIONS.map((option) => (
          <GlassChoiceButton
            key={option.value}
            Icon={option.Icon}
            label={t(option.labelKey)}
            sublabel={t(option.sublabelKey)}
            selected={selected === option.value}
            compact={compactChoices}
            onPress={() => handleSelect(option.value)}
          />
        ))}
      </View>
    </OnboardingLayout>
  );
}

const styles = StyleSheet.create({
  options: {
    gap: 14,
  },
  optionsCompact: {
    gap: 10,
  },
});
